import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Lock,
} from "lucide-react";

const NovelForm = ({
  title,
  setTitle,
  currentChapter,
  setCurrentChapter,
  chapterTitles,
  chapterContents,
  setChapterTitle,
  setChapterContent,
  selectedFont,
  selectedColor,
  backgroundOption,
  backgroundOptions,
}) => {
  const maxChapters = 10; // Updated: 10 chapters
  const maxChapterLength = 5000; // Updated: 5000 chars
  const minChapterLength = 50; // Minimum 50 chars

  // Check if a specific chapter is complete
  const isChapterComplete = (chapterIndex) => {
    return (
      chapterTitles[chapterIndex]?.trim() &&
      chapterContents[chapterIndex]?.trim() &&
      chapterContents[chapterIndex].trim().length >= minChapterLength
    );
  };

  // Check if current chapter is complete
  const isCurrentChapterComplete = () => {
    return isChapterComplete(currentChapter - 1);
  };

  // Check if a chapter is unlocked
  const isChapterUnlocked = (chapterNum) => {
    if (chapterNum === 1) return true;
    
    for (let i = 0; i < chapterNum - 1; i++) {
      if (!isChapterComplete(i)) {
        return false;
      }
    }
    return true;
  };

  // Navigate to previous chapter
  const goToPreviousChapter = () => {
    if (currentChapter > 1) {
      setCurrentChapter(currentChapter - 1);
    }
  };

  // Navigate to next chapter (only if unlocked)
  const goToNextChapter = () => {
    if (currentChapter < maxChapters && isChapterUnlocked(currentChapter + 1)) {
      setCurrentChapter(currentChapter + 1);
    }
  };

  const currentChapterIndex = currentChapter - 1;
  const currentChapterTitle = chapterTitles[currentChapterIndex] || "";
  const currentChapterContent = chapterContents[currentChapterIndex] || "";
  const remainingChars = maxChapterLength - currentChapterContent.length;
  const isNextChapterUnlocked = isChapterUnlocked(currentChapter + 1);

  return (
    <div className="space-y-6">
      {/* Novel Title */}
      <div>
        <div className="sm:hidden">
          <div className="relative">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your novel title..."
              className="w-full p-4 text-base bg-transparent border-0 border-b-2 border-black focus:outline-none focus:ring-0 placeholder-gray-500"
              maxLength={30}
            />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"></div>
          </div>
          <div className="text-xs text-gray-500 mt-2 px-1">
            {title.length}/30 characters
          </div>
        </div>
        
        <div className="hidden sm:block">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Novel Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter your novel title..."
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition shadow-sm"
            maxLength={30}
          />
          <div className="text-xs text-gray-500 mt-1">
            {title.length}/30 characters
          </div>
        </div>
      </div>

      {/* Chapter Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <BookOpen size={16} />
            Chapter Progress
          </h3>
          <span className="text-xs text-gray-500">
            {Array.from({ length: maxChapters }, (_, i) => i).filter(i => isChapterComplete(i)).length}/{maxChapters} completed
          </span>
        </div>
        
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
            const isUnlocked = isChapterUnlocked(num);
            const isComplete = isChapterComplete(num - 1);
            const isCurrent = currentChapter === num;
            
            return (
              <button
                key={num}
                onClick={() => isUnlocked && setCurrentChapter(num)}
                disabled={!isUnlocked}
                className={`relative h-10 rounded-lg border-2 transition-all flex items-center justify-center text-sm font-medium ${
                  isCurrent
                    ? "border-gray-900 bg-gray-900 text-white shadow-md"
                    : isComplete
                    ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                    : isUnlocked
                    ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                title={!isUnlocked ? "Complete previous chapters to unlock" : `Chapter ${num}`}
              >
                {!isUnlocked ? (
                  <Lock size={14} />
                ) : isComplete ? (
                  <CheckCircle2 size={16} />
                ) : (
                  num
                )}
              </button>
            );
          })}
        </div>
        
        <div className="mt-3 flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
          <AlertCircle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Complete each chapter (title + min 50 chars) to unlock the next one
          </p>
        </div>
      </div>

      {/* Chapter Navigation */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goToPreviousChapter}
            disabled={currentChapter === 1}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={16} />
            <span className="text-sm font-medium hidden sm:inline">Previous</span>
          </button>

          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 mb-1">Currently Editing</span>
            <span className="text-lg font-bold text-gray-900">
              Chapter {currentChapter}
            </span>
          </div>

          <button
            type="button"
            onClick={goToNextChapter}
            disabled={currentChapter === maxChapters || !isNextChapterUnlocked}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title={!isNextChapterUnlocked ? "Complete current chapter to unlock next" : ""}
          >
            <span className="text-sm font-medium hidden sm:inline">Next</span>
            {!isNextChapterUnlocked && currentChapter < maxChapters ? (
              <Lock size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        </div>

        {/* Progress indicator for current chapter */}
        {!isCurrentChapterComplete() && (
          <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-700 text-center">
              Complete this chapter to unlock Chapter {currentChapter + 1}
            </p>
          </div>
        )}
      </div>

      {/* Chapter Title */}
      <div>
        <div className="sm:hidden">
          <div className="relative">
            <input
              type="text"
              value={currentChapterTitle}
              onChange={(e) => setChapterTitle(currentChapterIndex, e.target.value)}
              placeholder={`Chapter ${currentChapter} title...`}
              className="w-full p-4 text-base bg-transparent border-0 border-b-2 border-black focus:outline-none focus:ring-0 placeholder-gray-500"
              maxLength={30}
            />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"></div>
          </div>
          <div className="text-xs text-gray-500 mt-2 px-1">
            {currentChapterTitle.length}/30 characters
          </div>
        </div>
        
        <div className="hidden sm:block">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Chapter {currentChapter} Title *
          </label>
          <input
            type="text"
            value={currentChapterTitle}
            onChange={(e) => setChapterTitle(currentChapterIndex, e.target.value)}
            placeholder={`Enter title for Chapter ${currentChapter}...`}
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition shadow-sm"
            maxLength={30}
          />
          <div className="text-xs text-gray-500 mt-1">
            {currentChapterTitle.length}/30 characters
          </div>
        </div>
      </div>

      {/* Chapter Content */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Chapter {currentChapter} Content *
          </label>
          <div className="text-xs flex items-center gap-2">
            <span
              className={`${
                remainingChars < 500 ? "text-red-500" : "text-gray-500"
              }`}
            >
              {currentChapterContent.length.toLocaleString()}/{maxChapterLength.toLocaleString()}
            </span>
            {currentChapterContent.length < minChapterLength && (
              <span className="text-yellow-600">
                (min: {minChapterLength})
              </span>
            )}
          </div>
        </div>

        <div className="sm:hidden">
          <div
            className={`relative border-t-2 border-b-2 border-black overflow-hidden ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              value={currentChapterContent}
              onChange={(e) => setChapterContent(currentChapterIndex, e.target.value)}
              placeholder={`Write Chapter ${currentChapter} content here...\n\nNo character limit per line for novels. Write freely!\n\nMinimum ${minChapterLength} characters required.`}
              className={`w-full p-2 text-[14px] bg-transparent focus:outline-none min-h-[300px] resize-y ${selectedFont} ${
                selectedColor === 'white' ? 'text-white' : 'text-black'
              } placeholder-gray-500`}
              maxLength={maxChapterLength}
            />
          </div>
        </div>
        
        <div className="hidden sm:block">
          <div
            className={`rounded-lg overflow-hidden border-2 border-gray-300 focus-within:border-gray-800 transition-colors ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              value={currentChapterContent}
              onChange={(e) => setChapterContent(currentChapterIndex, e.target.value)}
              placeholder={`Write Chapter ${currentChapter} content here...\n\nNo character limit per line for novels. Write freely!\n\nMinimum ${minChapterLength} characters required.`}
              className={`w-full p-4 bg-transparent focus:outline-none min-h-[400px] resize-y ${selectedFont} ${
                selectedColor === 'white' ? 'text-white' : 'text-black'
              } placeholder-gray-400 text-base`}
              maxLength={maxChapterLength}
            />
          </div>
        </div>

        {/* Character count warnings */}
        {currentChapterContent.length < minChapterLength && currentChapterContent.length > 0 && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-700">
              <strong>{minChapterLength - currentChapterContent.length} more characters</strong> needed to complete this chapter
            </p>
          </div>
        )}

        {/* Completion indicator */}
        {isCurrentChapterComplete() && (
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-700">
              <strong>Chapter {currentChapter} completed!</strong> 
              {currentChapter < maxChapters ? " You can now move to the next chapter." : " This is your final chapter."}
            </p>
          </div>
        )}
      </div>

      {/* Guidelines */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-start gap-2">
          <BookOpen size={16} className="text-gray-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-700">
            <p className="font-medium mb-2">Novel Writing Guidelines:</p>
            <ul className="space-y-1 list-disc list-inside text-gray-600">
              <li>Add up to 10 chapters to your novel</li>
              <li>Each chapter: 50 - 5,000 characters</li>
              <li>Complete chapters sequentially to unlock next</li>
              <li>No line length restrictions - write freely!</li>
              <li>Chapters must have both title and content</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovelForm;